/**
 * Inactivity Monitor with Auto-Initialization
 * 
 * Features:
 * - Auto-attaches window event listeners (click, keydown, scroll, touchstart)
 * - Runs asynchronously via IIFE - no manual component calls needed
 * - Automatically logs out user after configured timeout period
 * - Upload-aware pause/resume is handled externally via RTK listener middleware
 *   (see middleware/uploadInactivityListener.js), not by store.subscribe() here.
 * - Configuration from constants/inactivityConfig.js with .env override support
 

 *Configuration Priority: .env → constants 
 * - Default timeout: 5 minutes
 * - Default check interval: 30 seconds
 
 * Logging:
 * - All other internal state changes (activity resets, pause/resume) are silent
 *   to avoid excessive log noise.
 * - console.error is used only for unexpected failures in the inactivity check interval.
 */

import store from '../redux/store';
import { signOut } from '../redux/slices/authSlice';
import { INACTIVITY_CONFIG } from '../constants/inactivityConfig'
import logger from './logger';

let inactivityIntervalId = null;
let listenersAttached = false;
let lastActivityTimestamp = null;

const resetTimer = () => {
    lastActivityTimestamp = Date.now();
    
};




const activityHandler = () => {
    resetTimer();
};

/* Throttled mousemove — resets idle timer at most once every 5 seconds 
using lastActivityTimestamp itself
It checks if at least 5 seconds have passed since the last recorded activity (lastActivityTimestamp).
If so, it updates lastActivityTimestamp to the current time (Date.now()), 
effectively resetting the inactivity timer.
*/

const mouseMoveHandler = () => {
    const now = Date.now();
    if (now - (lastActivityTimestamp || 0) >= 5000) {
        lastActivityTimestamp = now;
    }
};

/*
 * Pauses the inactivity check timer.
 * Called by RTK listener middleware (uploadInactivityListener) when an upload starts.
 * Exported so the middleware can invoke it directly.
 */
export const pauseInactivityChecks = () => {
    if (inactivityIntervalId) {
        clearInterval(inactivityIntervalId);
        inactivityIntervalId = null;
    }
};

/***
 * Starts or restarts the inactivity check timer.
 * Called by RTK listener middleware (uploadInactivityListener) when uploads finish,
 * and also during initial monitor startup.
 * resetTimer() ensures the inactivity timer starts fresh when resumed,
 * preventing immediate logout if the user was idle during the upload.
 * Exported so the middleware can invoke it directly.
 */
export const startInactivityChecks = () => {
    if (inactivityIntervalId) {
        clearInterval(inactivityIntervalId);
    }
    resetTimer();

    inactivityIntervalId = setInterval(() => {
        try {
            const token = typeof window !== 'undefined' ? sessionStorage.getItem('bearer_token') : null;
            if (!token) {
                return;
            }

            if (!lastActivityTimestamp) {
                return;
            }

            const now = Date.now();
            const idleMs = now - lastActivityTimestamp;

            if (idleMs >= INACTIVITY_CONFIG.TIMEOUT_MS) {
                //if timer passed the configured timeout, log out the user
                logger.info(`User is being logged out due to inactivity. Idle for ${Math.round(idleMs / 1000)} seconds (timeout: ${Math.round(INACTIVITY_CONFIG.TIMEOUT_MS / 1000)} seconds).`);
                clearInterval(inactivityIntervalId);
                inactivityIntervalId = null;
                store.dispatch(signOut());
            }
        } catch (e) {
            console.error('Failed to handle inactivity check/logout:', e);
        }
    }, INACTIVITY_CONFIG.CHECK_INTERVAL_MS);
};

/*
 * startInactivityChecks: Starts the timer that logs out users if they're idle.
 * Upload pause/resume is now handled by RTK listener middleware (uploadInactivityListener.js),
 * replacing the previous store.subscribe() approach for better performance.
 * Auto-initialization via IIFE ensures the monitor starts without manual calls.
 */
const startInactivityMonitor = () => {
    if (typeof window === 'undefined') return;

    if (!listenersAttached) {
        window.addEventListener('click', activityHandler);
        window.addEventListener('keydown', activityHandler);
        window.addEventListener('scroll', activityHandler);
        window.addEventListener('mousemove', mouseMoveHandler);
        listenersAttached = true;
    }

    startInactivityChecks();
};

export const stopInactivityMonitor = () => {
    if (typeof window === 'undefined') return;

    if (listenersAttached) {
        window.removeEventListener('click', activityHandler);
        window.removeEventListener('keydown', activityHandler);
        window.removeEventListener('scroll', activityHandler);
        window.removeEventListener('mousemove', mouseMoveHandler);
        listenersAttached = false;
    }

    if (inactivityIntervalId) {
        clearInterval(inactivityIntervalId);
        inactivityIntervalId = null;
    }

    lastActivityTimestamp = null;
};

(async () => {
    if (typeof window !== 'undefined') {
        // Wait for the DOM to be ready
        if (document.readyState === 'loading') {
            await new Promise((resolve) => {
                document.addEventListener('DOMContentLoaded', resolve, { once: true });
            });
        }
        // Start the inactivity monitor automatically
        startInactivityMonitor();
    }
})();
